import { useTranslation } from 'react-i18next';
import { Label } from '@repo/ui/src/components/ui/label';
import { Input } from '@repo/ui/src/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@repo/ui/src/components/ui/select';
import type { Gender } from '@repo/shared-types';
import { DatePicker } from '../../../components/pickers/DatePicker';

export interface StudentIdentityValues {
  name: string;
  nickname: string;
  dateOfBirth: string; // '' or 'YYYY-MM-DD'
  gender: Gender | null;
}

/** Radix Select treats value="" as "nothing selected" and will not render an
 *  empty string as a choosable item, so clearing gender back to "not stated"
 *  needs a real sentinel value. It is mapped to null on the way out and
 *  produced from null on the way in, entirely inside this component. */
export const GENDER_UNSPECIFIED = 'unspecified';

export function StudentIdentityFields({
  values, onChange, idPrefix, markRequired,
}: {
  values: StudentIdentityValues;
  onChange: (patch: Partial<StudentIdentityValues>) => void;
  /** Prefixes every input id so two mounted dialogs cannot collide. */
  idPrefix: string;
  /** Show a red * beside the full-name label. */
  markRequired?: boolean;
}) {
  const { t } = useTranslation('students');

  return (
    <>
      <div className="grid gap-2">
        <Label htmlFor={`${idPrefix}-name`}>
          {t('fields.name')}
          {markRequired && <span aria-hidden="true" className="text-red-500 ml-0.5">*</span>}
        </Label>
        <Input
          id={`${idPrefix}-name`}
          value={values.name}
          onChange={(ev) => onChange({ name: ev.target.value })}
          placeholder={t('fields.namePlaceholder')}
          required
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={`${idPrefix}-nickname`}>{t('fields.nickname')}</Label>
        <Input
          id={`${idPrefix}-nickname`}
          value={values.nickname}
          onChange={(ev) => onChange({ nickname: ev.target.value })}
          placeholder={t('fields.nicknamePlaceholder')}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={`${idPrefix}-dob`}>{t('fields.dob')}</Label>
        <DatePicker
          id={`${idPrefix}-dob`}
          value={values.dateOfBirth}
          onChange={(value) => onChange({ dateOfBirth: value })}
          captionLayout="dropdown"
          fromYear={1950}
          toYear={new Date().getFullYear()}
          clearable
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor={`${idPrefix}-gender`}>{t('fields.gender')}</Label>
        <Select
          value={values.gender ?? GENDER_UNSPECIFIED}
          onValueChange={(v) => onChange({ gender: v === GENDER_UNSPECIFIED ? null : (v as Gender) })}
        >
          <SelectTrigger id={`${idPrefix}-gender`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={GENDER_UNSPECIFIED}>{t('fields.genderUnspecified')}</SelectItem>
            <SelectItem value="MALE">{t('fields.genderMale')}</SelectItem>
            <SelectItem value="FEMALE">{t('fields.genderFemale')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );
}
